import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { POST, GET } from "../../service/CRUDService";
import Toast from "../../common/Toast/Toast";
import CfLoader from "../../common/CfLoader/CfLoader";
import SimpleModal from "../../common/SimpleModal/SimpleModal";
import warningIcon from "../../resources/icons/warning-info.svg";
import CreateOrganisationUserModal from "../../common/CreateOrganisationUserModal/CreateOrganisationUserModal";
import EntityConstants from "../../configs/EntityConstants";
import { ChevronRight } from "lucide-react";
import useSearchDebounce from "../../hooks/useSearchDebounce";
import SimpleToggleButton from "../../common/SimpleToggleButton/SimpleToggleButton";
import AuthContext from "../../store/auth-context";

const TenantOrganisationPage = () => {
  const { tenant_code, organisation_code } = useParams();
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [limit] = useState(6);
  const [offset, setOffset] = useState(0);
  const [updatingUserEmail, setUpdatingUserEmail] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    user: null,
    currentStatus: false,
  });
  const [toggleRefresh, setToggleRefresh] = useState(false);

  const isTenantAdmin = authContext.userType?.toLowerCase() === "tenant_admin";

  const isTenantUser = authContext.userType?.toLowerCase() === "tenant_user";

  const performSearchAPI = async (searchTerm, signal) => {
    try {
      const endpoint = `${
        EntityConstants.GET_ORGANISATION_USERS
      }?org_code=${encodeURIComponent(
        organisation_code
      )}&limit=${limit}&offset=0&search=${encodeURIComponent(searchTerm)}`;
      const response = await GET(endpoint, { signal });
      const data = await response.json();

      if (data.success) {
        setUsers(data.data.results || []);
        setNext(data.data.next);
        setPrevious(data.data.previous);
        setOffset(0);
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: data.message || "Failed to search users",
        });
        setUsers([]);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Search users error:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to search users",
        });
        setUsers([]);
      }
    }
  };

  const { handleSearch } = useSearchDebounce(performSearchAPI, 500);

  const fetchUsers = async (
    currentOffset = 0,
    search = "",
    isToggle = false
  ) => {
    if (!isToggle) {
      setLoading(true);
    }
    try {
      const endpoint = search
        ? `${EntityConstants.GET_ORGANISATION_USERS}?org_code=${organisation_code}&limit=${limit}&offset=${currentOffset}&search=${search}`
        : `${EntityConstants.GET_ORGANISATION_USERS}?org_code=${organisation_code}&limit=${limit}&offset=${currentOffset}`;

      const response = await GET(endpoint);
      const data = await response.json();

      if (data.success) {
        setUsers(data.data.results || []);
        setNext(data.data.next);
        setPrevious(data.data.previous);
        setOffset(currentOffset);
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: data.message || "Failed to fetch organisation users",
        });
      }
    } catch (error) {
      console.error("Error fetching organisation users:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to fetch organisation users",
      });
    } finally {
      setLoading(false);
      if (isToggle) {
        setToggleRefresh(false);
      }
    }
  };

  useEffect(() => {
    if (tenant_code && organisation_code) {
      fetchUsers(0);
    }
  }, [tenant_code, organisation_code]);

  const handleUserToggle = (user) => {
    setConfirmModal({
      show: true,
      user: user,
      currentStatus: user.is_active,
    });
  };

  const confirmToggle = async () => {
    const { user, currentStatus } = confirmModal;
    if (!user) {
      cancelToggle();
      return;
    }

    setUpdatingUserEmail(user.email);
    try {
      const response = await POST(EntityConstants.POST_UPDATE_ORGANISATION, {
        org_code: organisation_code,
        email: user.email,
        status: !currentStatus,
      });

      const data = await response.json();

      if (data.success) {
        Toast({
          type: "success",
          title: "Success",
          message: data.message || "User status updated successfully",
        });
        setToggleRefresh(true);
        fetchUsers(offset, searchTerm, true);
      } else {
        Toast({
          type: "error",
          title: "Error",
          message: data.message || "Failed to update user status",
        });
      }
    } catch (error) {
      console.error("Error updating user status:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to update user status",
      });
    } finally {
      setUpdatingUserEmail(null);
      setConfirmModal({ show: false, user: null, currentStatus: false });
    }
  };

  const cancelToggle = () => {
    setConfirmModal({ show: false, user: null, currentStatus: false });
  };

  const handlePrevious = () => {
    if (previous !== null) {
      const prevOffset = Math.max(0, offset - limit);
      fetchUsers(prevOffset, searchTerm);
    }
  };

  const handleNext = () => {
    if (next !== null) {
      const nextOffset = offset + limit;
      fetchUsers(nextOffset, searchTerm);
    }
  };

  const handleUserCreated = () => {
    fetchUsers(offset, searchTerm);
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value ? e.target.value : "";
    setSearchTerm(value);
    setOffset(0);

    if (value.length > 0) {
      setIsSearching(true);
      handleSearch(value, {
        onStart: () => setIsSearching(true),
        onComplete: () => setIsSearching(false),
        onError: () => {
          setUsers([]);
          setIsSearching(false);
        },
        minChars: 1,
      });
    } else {
      setIsSearching(false);
      fetchUsers(0, "");
    }
  };

  const handleTenantClick = () => {
    navigate("/issuer/tenant");
  };

  const handleAdminClick = () => {
    navigate(`/issuer/tenant/${tenant_code}/organisations/users`);
  };

  return (
    <div className="flex flex-col items-center w-full pb-3 pt-20 bg-cfSecondary min-h-screen">
      <div
        className={`w-full bg-cfSearchContainer flex justify-center py-[0.8rem] px-4`}
      >
        <div
          className={`flex flex-col sm:flex-row items-start sm:items-center justify-between w-full`}
        >
          <div className="flex flex-col">
            <div className="flex items-center gap-2 text-cfHeadingText font-semibold text-lg self-start my-auto">
              <h2 className="text-lg font-medium flex items-center justify-center w-full">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  {!(isTenantAdmin || isTenantUser) && (
                    <>
                      <button
                        onClick={handleTenantClick}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleTenantClick();
                          }
                        }}
                        className="hover:text-gray-800 cursor-pointer focus:outline-none"
                      >
                        Tenant Management
                      </button>
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    </>
                  )}

                  <button
                    onClick={handleAdminClick}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleAdminClick();
                      }
                    }}
                    className="hover:text-gray-800 cursor-pointer focus:outline-none"
                  >
                    Admin Management
                  </button>
                  <ChevronRight className="h-4 w-4 text-gray-500" />
                  <span className="text-gray-800 font-medium">
                    Organisation Users
                  </span>
                </div>
              </h2>
            </div>
          </div>
          <div className="flex flex-col md:flex-row items-start gap-3">
            <button
              onClick={() => setShowCreateUserModal(true)}
              className={`px-4 py-2 bg-[#3538CD] text-white font-medium rounded-md hover:bg-blue-700 transition-colors duration-200`}
            >
              Create User
            </button>

            <input
              type="text"
              name="search"
              id="search"
              placeholder="Search for users"
              className="outline-none border border-gray-300 px-2 py-[2rem] rounded-lg placeholder:text-sm"
              style={{
                width: "350px",
                fontSize: "0.875rem",
                lineHeight: "1.4rem",
              }}
              value={searchTerm}
              onChange={handleSearchInputChange}
            />
          </div>
        </div>
      </div>

      <div className="w-full flex flex-col mt-4 px-4">
        <div className="overflow-x-auto">
          <div className="flex flex-col justify-center gap-2 min-w-full">
            <div className="flex bg-[#E0EAFF] items-center min-h-[4.5rem] border text-center min-w-[78.8rem] px-6 font-bold text-[14px]">
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                First Name
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                Last Name
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                Email
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                User Type
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                Status
              </div>
              <div className="px-6 py-3 text-xs text-cfTableHeaderText uppercase tracking-wider w-1/6">
                Action
              </div>
            </div>

            {isSearching && !toggleRefresh ? (
              <div className="flex justify-center items-center min-h-[8rem]">
                <CfLoader />
              </div>
            ) : loading ? (
              <div className="flex justify-center items-center min-h-[8rem]">
                <CfLoader />
              </div>
            ) : users.length === 0 ? (
              <div className="flex justify-center items-center w-full py-3 bg-cfSecondary text-gray-600 font-semibold text-lg">
                No users found
              </div>
            ) : (
              users.map((user) => (
                <div
                  key={user.email}
                  className="flex flex-row items-center border rounded-[var(--cf-issued-credential-border-radius)] py-[var(--cf-issued-credential-template-card-padding-y)] px-6 hover:bg-[#F9FAFB] min-h-[5rem] min-w-[78.8rem]"
                >
                  <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                    <p className="text-cfHeadingText font-semibold whitespace-nowrap truncate">
                      {user.first_name || "N/A"}
                    </p>
                  </div>
                  <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                    <p className="text-cfHeadingText truncate">
                      {user.last_name || "N/A"}
                    </p>
                  </div>
                  <div className="w-1/6 text-center min-w-[200px] flex-shrink-0 overflow-hidden">
                    <p className="text-cfHeadingText truncate">{user.email}</p>
                  </div>
                  <div className="w-1/6 text-center min-w-[150px] flex-shrink-0 overflow-hidden">
                    <p className="text-cfHeadingText truncate">
                      {user.user_type}
                    </p>
                  </div>
                  <div className="w-1/6 text-center min-w-[100px] flex-shrink-0 overflow-hidden">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        user.is_active
                          ? "bg-cfIssuedCredentialTemplateCardStatusDoneBG text-cfIssuedCredentialTemplateCardStatusText"
                          : "bg-cfIssuedCredentialTemplateCardStatusRevokedBG text-white"
                      }`}
                    >
                      {user.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="w-1/6 text-center min-w-[100px] flex-shrink-0 overflow-hidden">
                    <SimpleToggleButton
                      checked={user.is_active}
                      onChange={() => handleUserToggle(user)}
                      disabled={updatingUserEmail === user.email}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="flex items-center justify-between mt-6 w-full">
          <button
            onClick={handlePrevious}
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            disabled={previous === null}
          >
            <span className="flex items-center justify-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 rtl:-scale-x-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 15.75L3 12m0 0l3.75-3.75M3 12h18"
                />
              </svg>
              <span>previous</span>
            </span>
          </button>

          <button
            onClick={handleNext}
            className="flex items-center justify-center px-5 py-2 w-36 text-sm active:text-gray-700 capitalize transition-colors duration-200 bg-cfSecondary border rounded-md gap-x-2 hover:bg-gray-100 disabled:text-gray-400"
            disabled={next === null}
          >
            <span className="flex items-center justify-center gap-2">
              <span>Next</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="w-5 h-5 rtl:-scale-x-100"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M17.25 8.25L21 12m0 0l-3.75 3.75M21 12H3"
                />
              </svg>
            </span>
          </button>
        </div>
      </div>

      <CreateOrganisationUserModal
        isOpen={showCreateUserModal}
        onClose={() => setShowCreateUserModal(false)}
        onUserCreated={handleUserCreated}
        tenantCode={tenant_code}
        orgCode={organisation_code}
      />

      <SimpleModal
        isOpen={confirmModal.show}
        closeModal={cancelToggle}
        successFunction={confirmToggle}
        modalIcon={warningIcon}
        modalHeaderMessage={`${
          confirmModal.currentStatus ? "Deactivate" : "Activate"
        } User`}
        modalBodyMessage={`Are you sure you want to ${
          confirmModal.currentStatus ? "deactivate" : "activate"
        } "${confirmModal.user?.first_name} ${confirmModal.user?.last_name}"?`}
        leftButtonMessage="Cancel"
        rightButtonMessage="Confirm"
      />
    </div>
  );
};

export default TenantOrganisationPage;
